import { AfterViewInit, Directive, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { auditTime, map, startWith } from 'rxjs/operators';

@Directive({ selector: '[hfAutoScroll]', exportAs: 'hfAutoScroll' })
export class AutoScrollDirective implements AfterViewInit, OnDestroy {
  @Input() autoScrollThreshold = 80; // px from bottom
  @Input() autoScrollBehavior: ScrollBehavior = 'smooth';
  @Output() contentPainted = new EventEmitter<void>();

  private atBottom = true;
  private sub = new Subscription();
  private mo?: MutationObserver;
  private ro?: ResizeObserver;

  private paintScheduled = false;
  private pauseUntil = 0;

  constructor(
    private host: ElementRef<HTMLElement>,
    private zone: NgZone
  ) {}

  private get paused() {
    return Date.now() < this.pauseUntil;
  }

  ngAfterViewInit() {
    const el = this.host.nativeElement;
    this.zone.runOutsideAngular(() => {
      const scroll$ = fromEvent(el, 'scroll')
        .pipe(
          startWith(null),
          map(() => this.isNearBottom(el, this.autoScrollThreshold)),
          auditTime(60)
        )
        .subscribe((isNear) => (this.atBottom = isNear));
      this.sub.add(scroll$);

      this.mo = new MutationObserver(() => this.scrollIfNeeded());
      this.mo.observe(el, { childList: true, subtree: true });

      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.scrollIfNeeded());
        this.ro.observe(el);
      }

      requestAnimationFrame(() => this.scrollIfNeeded(true));

      const wheel$ = fromEvent<WheelEvent>(el, 'wheel', { passive: true } as any).subscribe((e) => {
        // Only pause if user is trying to scroll UP
        if (e.deltaY < 0) this.pause();
      });

      const touch$ = fromEvent<TouchEvent>(el, 'touchmove', { passive: true } as any).subscribe(() => this.pause());

      const key$ = fromEvent<KeyboardEvent>(el, 'keydown').subscribe((e) => {
        const keys = ['ArrowUp', 'PageUp', 'Home'];
        if (keys.includes(e.key)) this.pause();
      });

      this.sub.add(wheel$);
      this.sub.add(touch$);
      this.sub.add(key$);
    });
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
    this.mo?.disconnect();
    this.ro?.disconnect();
  }

  /** Scroll only if the user is currently "pinned" near the bottom */
  public forceScrollIfPinned() {
    if (!this.atBottom) return;
    this.scrollIfNeeded(true);
    this.schedulePaintedPing();
  }

  private pause(ms = 1200) {
    this.pauseUntil = Date.now() + ms;
    this.atBottom = false; // immediately unpin so we stop fighting the user
  }

  private isNearBottom(el: HTMLElement, px: number) {
    return el.scrollHeight - el.scrollTop - el.clientHeight <= px;
  }

  private scrollIfNeeded(force = false) {
    const el = this.host.nativeElement;

    if (!force && (this.paused || !this.atBottom)) return;

    try {
      el.scrollTo({ top: el.scrollHeight, behavior: this.autoScrollBehavior });
    } catch {
      el.scrollTop = el.scrollHeight;
    }
  }

  private schedulePaintedPing() {
    if (this.paintScheduled) return;
    this.paintScheduled = true;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.paintScheduled = false;
        this.zone.run(() => this.contentPainted.emit());
      });
    });
  }
}
